import React from 'react';
import { useLocaleContext } from 'context/LocaleContext';
import { config } from 'config/config';

type LocaleTextMap = Record<string, string>;
type LocaleTextInput = string | LocaleTextMap;

interface LocaleTextInterface {
    text?: LocaleTextInput | null;
    defaultText?: string;
}

const normalizeLocaleKey = (value: string): string => value.trim().toLowerCase();

const LocaleText: React.FC<LocaleTextInterface> = ({ text, defaultText }) => {
    const { locale } = useLocaleContext();

    let localeText = !!defaultText ? defaultText : '';

    if (typeof text === 'string') {
        // Some APIs return already-localized strings; prefer explicit defaultText if provided.
        localeText = localeText || text;
    } else if (text) {
        const defaultLocale = config.api.defaultAppLanguage;
        const candidates = [
            locale,
            locale?.toLowerCase(),
            locale?.toUpperCase(),
            locale?.split('-')[0],
            locale?.split('-')[0]?.toLowerCase(),
            locale?.split('-')[0]?.toUpperCase(),
            defaultLocale,
            defaultLocale?.toLowerCase(),
            defaultLocale?.toUpperCase(),
        ].filter((v): v is string => !!v);

        // Case-insensitive matching because some APIs return locale keys like "eN", "lV".
        const normalized = Object.entries(text).reduce<Record<string, string>>((acc, [k, v]) => {
            acc[normalizeLocaleKey(k)] = v;
            return acc;
        }, {});

        for (const key of candidates) {
            const value = normalized[normalizeLocaleKey(key)];
            if (value) {
                localeText = value;
                break;
            }
        }

        if (!localeText) {
            const firstKey = Object.keys(text)[0];
            if (firstKey) {
                localeText = text[firstKey] ?? localeText;
            }
        }
    }

    return <>{localeText}</>;
};

export default LocaleText;
